import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import ModalLinkLocationsConfirm from "./ModalLinkLocationsConfirm";
import { LocationData } from "../../../models/LocationData";
import { OrganizationData } from "../../../models/OrganizationData";
import DataSvc from "../../../services/dataSvc";
import { Checkbox } from "../../FormElement/Checkbox";
import { InfiniteScroller } from "../../InfiniteScroller";
import "./styles.scss";

export interface IModalLinkLocationsProps {
  organization: OrganizationData;
  onClose: () => void;
  onSuccess: () => void;
}

export const ModalLinkLocations: React.FunctionComponent<
  IModalLinkLocationsProps
> = (props) => {
  const { organization, onClose, onSuccess } = props;
  const { t: _t } = useTranslation();

  const t = (key: string, params?: any) =>
    _t(`organizationManagementPage.modalLinkLocations.${key}`, params);

  // States for Search Filter Textareas
  const [searchName, setSearchName] = useState("");
  const [searchLoc, setSearchLoc] = useState("");
  const [isApplied, setIsApplied] = useState(true);

  // API query params that trigger getLocations for Lower List
  const [queryParams, setQueryParams] = useState<any>({});

  // State to hold Original Assigned Locations (Upper List)
  const [originalLocations, setOriginalLocations] = useState<LocationData[]>(
    []
  );
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(true);

  // Set of Selected Location IDs (both upper and lower)
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Show Confirmation Modal state
  const [shownConfirmModal, setShownConfirmModal] = useState(false);

  // Fetch Original Assigned Locations on mount
  useEffect(() => {
    let active = true;
    const fetchOriginal = async () => {
      setIsLoadingOriginal(true);
      try {
        const res = await DataSvc.getOrganization({
          organizationId: organization.id,
        });
        if (active) {
          const list = res.locations || [];
          setOriginalLocations(list);
          setSelectedIds(list.map((loc) => String(loc.id)));
        }
      } catch (err) {
        console.error("Failed to fetch original locations", err);
      } finally {
        if (active) {
          setIsLoadingOriginal(false);
        }
      }
    };
    fetchOriginal();
    return () => {
      active = false;
    };
  }, [organization.id]);

  // State to hold all items fetched by InfiniteScroller
  const [scrollerItems, setScrollerItems] = useState<LocationData[]>([]);

  const dependencyKey = useMemo(() => {
    return `${isApplied}_${queryParams.name || ""}_${
      queryParams.searchLoc || ""
    }`;
  }, [isApplied, queryParams]);

  useEffect(() => {
    setScrollerItems([]);
  }, [dependencyKey]);

  // Fetch function adapted for InfiniteScroller
  const fetchLocationsForInfiniteScroller = async (params: any) => {
    if (!isApplied) {
      return { data: [], count: 0 };
    }

    const name = queryParams.name;
    const searchLocVal = queryParams.searchLoc;
    const limit = params.limit || 50;
    const offset = params.offset || 0;

    let fetchedList: LocationData[] = [];
    let totalCount = 0;

    if (searchLocVal) {
      const [resPrefecture, resCity] = await Promise.all([
        DataSvc.getLocations({
          limit: 1000,
          ...(name ? { name } : {}),
          prefecture: searchLocVal,
        }),
        DataSvc.getLocations({
          limit: 1000,
          ...(name ? { name } : {}),
          city: searchLocVal,
        }),
      ]);

      const listPrefecture = resPrefecture?.list || [];
      const listCity = resCity?.list || [];

      const mergedMap = new Map<number, LocationData>();
      listPrefecture.forEach((loc) => mergedMap.set(loc.id, loc));
      listCity.forEach((loc) => mergedMap.set(loc.id, loc));

      const mergedList = Array.from(mergedMap.values());
      totalCount = mergedList.length;
      fetchedList = mergedList.slice(offset, offset + limit);
    } else {
      const res = await DataSvc.getLocations({
        limit,
        offset,
        ...(name ? { name } : {}),
      });
      fetchedList = res?.list || [];
      totalCount = res?.total || 0;
    }

    setScrollerItems((prev) => {
      if (offset === 0) {
        return fetchedList;
      }
      const mergedMap = new Map(prev.map((item) => [item.id, item]));
      fetchedList.forEach((item) => mergedMap.set(item.id, item));
      return Array.from(mergedMap.values());
    });

    return {
      data: fetchedList,
      count: totalCount,
    };
  };

  const searchedLocationsList = useMemo(() => {
    if (!isApplied) return [];
    return scrollerItems;
  }, [scrollerItems, isApplied]);

  // Lower List: Filtered locations from query that are NOT in the original list
  const lowerList = useMemo(() => {
    if (!isApplied) return [];
    const originalIds = new Set(originalLocations.map((loc) => String(loc.id)));
    return searchedLocationsList.filter(
      (loc) => !originalIds.has(String(loc.id))
    );
  }, [searchedLocationsList, originalLocations, isApplied]);

  // Apply Filter
  const handleApply = () => {
    const params: any = {};
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    if (searchLoc.trim()) {
      params.searchLoc = searchLoc.trim();
    }
    setQueryParams(params);
    setIsApplied(true);
  };

  // Clear Filter
  const handleClear = () => {
    setSearchName("");
    setSearchLoc("");
    setQueryParams({});
    setIsApplied(true);
  };

  // Checkbox toggle handler
  const handleCheckChange = (locId: number, checked: boolean) => {
    const idStr = String(locId);
    setSelectedIds((prev) => {
      if (checked) {
        if (prev.includes(idStr)) return prev;
        return [...prev, idStr];
      } else {
        return prev.filter((id) => id !== idStr);
      }
    });
  };

  // Reset Button
  const handleReset = () => {
    setSelectedIds(originalLocations.map((loc) => String(loc.id)));
  };

  // Uncheck All currently visible locations on the screen
  const handleUncheckAll = () => {
    const visibleIds = new Set([
      ...originalLocations.map((loc) => String(loc.id)),
      ...lowerList.map((loc) => String(loc.id)),
    ]);
    setSelectedIds((prev) => prev.filter((id) => !visibleIds.has(id)));
  };

  // Check All currently visible locations on the screen
  const handleCheckAll = () => {
    const visibleIds = [
      ...originalLocations.map((loc) => String(loc.id)),
      ...lowerList.map((loc) => String(loc.id)),
    ];
    setSelectedIds((prev) => {
      const next = new Set([...prev, ...visibleIds]);
      return Array.from(next);
    });
  };

  // Prepare modifications list for Confirmation Modal
  const addedLocations = useMemo(() => {
    const originalIds = new Set(originalLocations.map((loc) => String(loc.id)));
    // Gather all currently selected locations that were NOT originally assigned
    // We search among both lowerList and other searched locations to show complete detail
    const allAvailable = [...originalLocations, ...searchedLocationsList];
    const uniqueMap = new Map<string, LocationData>();
    allAvailable.forEach((loc) => uniqueMap.set(String(loc.id), loc));

    return selectedIds
      .filter((id) => !originalIds.has(id))
      .map((id) => uniqueMap.get(id))
      .filter((loc): loc is LocationData => !!loc);
  }, [selectedIds, originalLocations, searchedLocationsList]);

  const removedLocations = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    return originalLocations.filter((loc) => !selectedSet.has(String(loc.id)));
  }, [selectedIds, originalLocations]);

  // Click Save (opens confirm dialog)
  const handleConfirmClick = () => {
    setShownConfirmModal(true);
  };

  // Final submit to API on confirmation
  const handleFinalSubmit = async () => {
    try {
      const orgIdNum = Number(organization.id);

      const addPromises = addedLocations.map((loc) =>
        DataSvc.registerLocationWithOrg(loc.id, orgIdNum)
      );
      const removePromises = removedLocations.map((loc) =>
        DataSvc.deregisterLocationWithOrg(loc.id, orgIdNum)
      );

      await Promise.all([...addPromises, ...removePromises]);
      toast.success(t("save_success_toast"));
      setShownConfirmModal(false);
      onSuccess();
    } catch (error) {
      console.error("Failed to update organization locations mapping", error);
      toast.error(t("save_failed_toast"));
    }
  };

  return (
    <div className="modal modal-default modal-link-locations">
      <div className="modal-mains">
        <button
          className="btn-close"
          aria-label="Close"
          onClick={onClose}
        ></button>
        <div className="top-title">
          {t("title", { orgName: organization.name })}
        </div>

        {/* Top Search filters (Two textareas) */}
        <div className="search-section">
          <div className="search-row">
            <div className="textarea-wrapper">
              <span className="icons icon-search"></span>
              <textarea
                className="search-textarea"
                placeholder={t("search_name_placeholder")}
                value={searchName}
                onChange={(e) =>
                  setSearchName(e.target.value.replace(/\r?\n/g, ""))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleApply();
                  }
                }}
                rows={1}
              />
            </div>
          </div>
          <div className="search-row">
            <div className="textarea-wrapper">
              <span className="icons icon-search"></span>
              <textarea
                className="search-textarea"
                placeholder={t("search_loc_placeholder")}
                value={searchLoc}
                onChange={(e) =>
                  setSearchLoc(e.target.value.replace(/\r?\n/g, ""))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleApply();
                  }
                }}
                rows={1}
              />
            </div>
          </div>
          <div className="search-buttons-row">
            <button className="btn btn-blue btn-apply" onClick={handleApply}>
              {t("apply")}
            </button>
            {isApplied && (
              <button className="btn btn-clear-filter" onClick={handleClear}>
                {t("clear")}
              </button>
            )}
          </div>
        </div>

        {/* Middle action buttons */}
        <div className="middle-actions">
          <button className="btn btn-reset" onClick={handleReset}>
            {t("reset")}
          </button>
          <div className="right-group">
            <button
              className="btn btn-blue rounded-btn"
              onClick={handleUncheckAll}
            >
              {t("uncheck_all")}
            </button>
            <button
              className="btn btn-blue rounded-btn"
              onClick={handleCheckAll}
            >
              {t("check_all")}
            </button>
          </div>
        </div>

        {/* Lists sections */}
        <div className="lists-container">
          {/* Upper List: Currently Assigned Locations */}
          <div className="list-section">
            <div className="list-title">{t("assigned_list_title")}</div>
            <div className="list-scrollable">
              {isLoadingOriginal ? (
                <div className="loading-text">{t("loading")}</div>
              ) : originalLocations.length === 0 ? (
                <div className="empty-text">{t("no_assigned_locations")}</div>
              ) : (
                originalLocations.map((loc) => (
                  <div className="list-item" key={loc.id}>
                    <Checkbox
                      checked={selectedIds.includes(String(loc.id))}
                      id={`checkbox-assigned-${loc.id}`}
                      label={loc.name}
                      onChange={(checked) => handleCheckChange(loc.id, checked)}
                    />
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Lower List: Filtered/Other Locations */}
          <div className="list-section">
            <div className="list-title">{t("other_list_title")}</div>
            {!isApplied ? (
              <div className="list-scrollable">
                <div className="empty-text">{t("no_other_locations")}</div>
              </div>
            ) : (
              <InfiniteScroller
                fetchFunction={fetchLocationsForInfiniteScroller}
                initialParams={{ offset: 0, limit: 50 }}
                pageSize={50}
                dependencyKey={dependencyKey}
                direction="vertical"
                className="list-scrollable-scroller"
                renderItem={(loc: LocationData) => {
                  const originalIds = new Set(
                    originalLocations.map((item) => String(item.id))
                  );
                  if (originalIds.has(String(loc.id))) {
                    return null;
                  }
                  return (
                    <div className="list-item" key={loc.id}>
                      <Checkbox
                        checked={selectedIds.includes(String(loc.id))}
                        id={`checkbox-other-${loc.id}`}
                        label={loc.name}
                        onChange={(checked) =>
                          handleCheckChange(loc.id, checked)
                        }
                      />
                      {(loc.prefecture || loc.city) && (
                        <span className="location-badge">
                          {`${loc.prefecture || ""} • ${loc.city || ""}`}
                        </span>
                      )}
                    </div>
                  );
                }}
                noItemsComponent={
                  <div className="empty-text">{t("no_other_locations")}</div>
                }
                loadingComponent={
                  <div className="loading-text">{t("loading")}</div>
                }
              />
            )}
          </div>
        </div>

        {/* Bottom primary actions */}
        <div className="bottom-btns">
          <button className="btn btn-border rounded-btn" onClick={onClose}>
            {t("cancel")}
          </button>
          <button
            className="btn btn-blue rounded-btn"
            onClick={handleConfirmClick}
          >
            {t("confirm")}
          </button>
        </div>
      </div>

      {/* Confirmation Sub-Modal */}
      {shownConfirmModal && (
        <ModalLinkLocationsConfirm
          orgName={organization.name}
          addedLocations={addedLocations}
          removedLocations={removedLocations}
          onClose={() => setShownConfirmModal(false)}
          onConfirm={handleFinalSubmit}
        />
      )}
    </div>
  );
};

export default ModalLinkLocations;
